/**
 * Custom hook for handling canvas resize events
 */

import { useEffect, useCallback, useRef, RefObject } from "react";
import { setupCanvas } from "./canvasRenderer";
import { CANVAS_SCALE, TREE_ASPECT_RATIO } from "./constants";
import type { CanvasConfig } from "./types";

// Cap canvas resolution for performance on high-res displays
const MAX_CANVAS_HEIGHT = 1080;

interface UseCanvasResizeOptions {
  canvasRef: RefObject<HTMLCanvasElement>;
  onResize: (config: CanvasConfig) => void;
  debounceMs?: number;
}

export function useCanvasResize({
  canvasRef,
  onResize,
  debounceMs = 100,
}: UseCanvasResizeOptions): void {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleResize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate canvas dimensions with fixed aspect ratio
    // Use height as the base and calculate width from aspect ratio
    let canvasWidth: number;
    let canvasHeight: number;

    // Determine dimensions based on which would fill the viewport better
    const heightBasedWidth = viewportHeight * TREE_ASPECT_RATIO;

    if (heightBasedWidth >= viewportWidth) {
      // Use height as base (viewport is relatively tall)
      canvasHeight = viewportHeight * CANVAS_SCALE;
      canvasWidth = canvasHeight * TREE_ASPECT_RATIO;
    } else {
      // Use width as base (viewport is relatively wide)
      canvasWidth = viewportWidth * CANVAS_SCALE;
      canvasHeight = canvasWidth / TREE_ASPECT_RATIO;
    }

    // Cap resolution for performance on 4K+ displays
    if (canvasHeight > MAX_CANVAS_HEIGHT) {
      const scaleFactor = MAX_CANVAS_HEIGHT / canvasHeight;
      canvasHeight = MAX_CANVAS_HEIGHT;
      canvasWidth = canvasWidth * scaleFactor;
    }

    const dpr = window.devicePixelRatio || 1;

    // Setup canvas with proper DPI scaling
    const ctx = setupCanvas(canvas, canvasWidth, canvasHeight);
    if (!ctx) {
      console.error("Failed to setup canvas on resize");
      return;
    }

    // Notify parent of new dimensions
    onResize({
      width: canvasWidth,
      height: canvasHeight,
      dpr,
    });
  }, [canvasRef, onResize]);

  const debouncedResize = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      handleResize();
    }, debounceMs);
  }, [handleResize, debounceMs]);

  useEffect(() => {
    // Initial setup (no debounce for first render)
    handleResize();

    // Create ResizeObserver for element-specific resize handling
    let resizeObserver: ResizeObserver | null = null;

    // Check if ResizeObserver is actually available (not just in types)
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(debouncedResize);
      if (canvasRef.current) {
        resizeObserver.observe(
          canvasRef.current.parentElement || canvasRef.current
        );
      }
    }

    // Always listen to window resize events as well
    // This ensures we catch window resizes even if the parent element doesn't change size
    window.addEventListener("resize", debouncedResize);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener("resize", debouncedResize);
    };
  }, [handleResize, debouncedResize, canvasRef]);
}
